import trapFocus from '../../utils/trapFocus';
import initAtcPopup from '../../components/atc-popup';
import { debounce } from 'throttle-debounce';
import { SETTINGS, CLASSES, ARIAS } from '../../utils/constants';

const PRODUCT_GRID_SELECTOR = '[data-product-grid]';
const PRODUCT_GRID_ITEMS_SELECTOR = '[data-product-grid-items]';
const PAGINATION_SELECTOR = '[data-pagination]';
const TIMEOUT = 3000;

let isFilterClickDelegationInitialized = false;
const filterHandlerMap = new WeakMap();

const initCollectionSearchFilters = () => {
  const filtersContainer = document.querySelector('[data-filters-container]');
  if (!filtersContainer) return;
  if (filtersContainer.dataset.filtersInitialized) return;
  filtersContainer.dataset.filtersInitialized = 'true';

  const productGridEl = document.querySelector(PRODUCT_GRID_SELECTOR);
  const paginationEl = document.querySelector(PAGINATION_SELECTOR);
  const filterDrawer = filtersContainer.querySelector('[data-filter-drawer]');
  const openDrawerBtn = filtersContainer.querySelector('[data-open-filter-drawer]');
  const closeDrawerBtn = filtersContainer.querySelector('[data-close-filter-drawer-button]');
  const filterFormEl = filtersContainer.querySelector('[data-filter-form]');
  const filterDrawerSubmitBtn = filtersContainer.querySelector('[data-filters-submit]');  
  const filterDrawerClearAllBtn = filtersContainer.querySelector('[data-filters-clear-all]');  
  const sortByEl = filtersContainer.querySelector('[data-sort-by-select]');
  const resultsCount = filtersContainer.querySelector('[data-filters-results-count]');
  const activeFilterCount = filtersContainer.querySelector('[data-active-filter-count]');
  const desktopFilterControlsEl = filtersContainer.querySelector('[data-desktop-filter-controls]');

  const sectionId = filterDrawer.dataset.sectionId;

  const PRICE_RANGE_SELECTOR = '[data-price-range]';
  const ACCORDION_TOGGLE_SELECTOR = '[data-filter-accordion-toggle]';
  const ACTIVE_COUNT_SELECTOR = '[data-filter-active-count]';

  const formatPriceRangeValue = (value, currencySymbol) => {
    const numberValue = Number(value);
    return `${currencySymbol}${Math.round(numberValue)}`;
  };

  const initPriceRangeSliders = () => {
    const priceRangeEls = filtersContainer.querySelectorAll(PRICE_RANGE_SELECTOR);

    priceRangeEls.forEach(priceRangeEl => {
      const minRangeEl = priceRangeEl.querySelector('[data-price-range-min]');
      const maxRangeEl = priceRangeEl.querySelector('[data-price-range-max]');
      const minInputEl = priceRangeEl.querySelector('[data-price-range-min-input]');
      const maxInputEl = priceRangeEl.querySelector('[data-price-range-max-input]');
      const minLabelEl = priceRangeEl.querySelector('[data-price-range-min-label]');
      const maxLabelEl = priceRangeEl.querySelector('[data-price-range-max-label]');
      const trackEl = priceRangeEl.querySelector('[data-price-range-track]');

      if (!minRangeEl || !maxRangeEl || !minInputEl || !maxInputEl || !minLabelEl || !maxLabelEl || !trackEl) return;

      const currencySymbol = priceRangeEl.dataset.currencySymbol || '';
      const minLimit = Number(minRangeEl.min);
      const maxLimit = Number(maxRangeEl.max);
      const priceRangeLimit = maxLimit - minLimit;

      const syncPriceRange = (changedRangeEl) => {
        let minValue = Number(minRangeEl.value);
        let maxValue = Number(maxRangeEl.value);

        if (minValue > maxValue) {
          if (changedRangeEl === minRangeEl) {
            maxValue = minValue;
            maxRangeEl.value = maxValue;
          } else {
            minValue = maxValue;
            minRangeEl.value = minValue;
          }
        }

        minInputEl.value = minValue <= minLimit ? '' : Math.round(minValue);
        maxInputEl.value = maxValue >= maxLimit ? '' : Math.round(maxValue);
        minLabelEl.textContent = formatPriceRangeValue(minValue, currencySymbol);
        maxLabelEl.textContent = formatPriceRangeValue(maxValue, currencySymbol);

        if (priceRangeLimit <= 0) return;
        const minPercent = ((minValue - minLimit) / priceRangeLimit) * 100;
        const maxPercent = ((maxValue - minLimit) / priceRangeLimit) * 100;
        const style = getComputedStyle(trackEl);
        const colorLight = style.getPropertyValue('--color-light-grey').trim();
        const colorDark = style.getPropertyValue('--color-charcoal').trim();
        trackEl.style.background = `linear-gradient(to right, ${colorLight} ${minPercent}%, ${colorDark} ${minPercent}%, ${colorDark} ${maxPercent}%, ${colorLight} ${maxPercent}%)`;
      };

      if (!priceRangeEl.dataset.priceRangeInitialized) {
        minRangeEl.addEventListener('input', () => syncPriceRange(minRangeEl));
        maxRangeEl.addEventListener('input', () => syncPriceRange(maxRangeEl));
        priceRangeEl.dataset.priceRangeInitialized = 'true';
      }
      syncPriceRange();
    });
  };

  const fetchResults = async (url) => {
    try {
      const response = await fetch(url);
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (err) {
      console.error('Error fetching filter results:', err);
    }
  };

  const getSectionUrl = (url) => {
    const sectionUrl = new URL(url, window.location.origin);
    if (sectionId) {
      sectionUrl.searchParams.set('section_id', sectionId);
    }
    return sectionUrl.href;
  };

  const refreshProductCardEnhancements = () => {
    initAtcPopup();
    if (typeof window.ReloadSmartWishlist === 'function') {
      ReloadSmartWishlist();
    }
  };

  const renderProductGrid = (resultsDoc) => {
    if (!resultsDoc) return;

    const mappings = [
      { oldEl: productGridEl, newEl: PRODUCT_GRID_SELECTOR },
      { oldEl: paginationEl, newEl: PAGINATION_SELECTOR },
      { oldEl: resultsCount, newEl: '[data-filters-results-count]' },
      { oldEl: activeFilterCount, newEl: '[data-active-filter-count]' },
      { oldEl: desktopFilterControlsEl, newEl: '[data-desktop-filter-controls]' },
    ];
    
    mappings.forEach(({ oldEl, newEl }) => {
      const newContent = resultsDoc.querySelector(newEl);
      if (oldEl && newContent) oldEl.innerHTML = newContent.innerHTML;
    });

    refreshProductCardEnhancements();
  };

  // The AJAX filter update reconciles inputs in place but does not re-render the
  // accordion headers, so the server-rendered active-count badge is never
  // applied. Sync each header's badge from the fetched document. Runs on every
  // in-drawer interaction, so it must NOT touch the expanded/collapsed state,
  // or the accordion the user is filtering in would snap shut mid-selection.
  const syncFilterAccordionBadges = (newFiltersFormEl) => {
    const existingAccordionEls = filterFormEl.querySelectorAll(ACCORDION_TOGGLE_SELECTOR);
    const newAccordionEls = [...newFiltersFormEl.querySelectorAll(ACCORDION_TOGGLE_SELECTOR)];
    existingAccordionEls.forEach(existingToggle => {
      const controlsId = existingToggle.getAttribute(ARIAS.CONTROLS);
      if (!controlsId) return;
      const existingBadgeEl = existingToggle.querySelector(ACTIVE_COUNT_SELECTOR);
      const newToggle = newAccordionEls.find(
        toggle => toggle.getAttribute(ARIAS.CONTROLS) === controlsId
      );
      // Accordion no longer in the fetched results — drop any stale badge.
      if (!newToggle) {
        if (existingBadgeEl) existingBadgeEl.remove();
        return;
      }
      const newBadgeEl = newToggle.querySelector(ACTIVE_COUNT_SELECTOR);
      if (newBadgeEl) {
        if (existingBadgeEl) {
          existingBadgeEl.textContent = newBadgeEl.textContent;
        } else {
          existingToggle.querySelector('[data-filter-label]')?.append(newBadgeEl.cloneNode(true));
        }
      } else if (existingBadgeEl) {
        existingBadgeEl.remove();
      }
    });
  };

  // Reset every accordion to collapsed so the drawer always opens in a clean,
  // fully-collapsed state regardless of what the user last left open. Run on open
  // rather than close so in-drawer AJAX updates never collapse a panel the user is
  // filtering in; the active-count badge keeps applied filters discoverable once
  // collapsed.
  const collapseAllAccordions = () => {
    filterFormEl.querySelectorAll(ACCORDION_TOGGLE_SELECTOR).forEach(toggle => {
      toggle.setAttribute(ARIAS.EXPANDED, 'false');
      const controlsId = toggle.getAttribute(ARIAS.CONTROLS);
      if (!controlsId) return;
      const panelEl = filterFormEl.querySelector(`#${controlsId}`);
      if (panelEl) panelEl.hidden = true;
    });
  };

  const renderFilters = (resultsDoc) => {
    if (!resultsDoc) return;

    const newFiltersSubmitBtn = resultsDoc.querySelector('[data-filters-submit]');
    if (newFiltersSubmitBtn) {
      filterDrawerSubmitBtn.innerHTML = newFiltersSubmitBtn.innerHTML;
    }
    const newFiltersFormEl = resultsDoc.querySelector('[data-filter-form]');
    if (newFiltersFormEl) {
      const newPriceRangeEls = newFiltersFormEl.querySelectorAll(PRICE_RANGE_SELECTOR);
      const existingPriceRangeEls = filterFormEl.querySelectorAll(PRICE_RANGE_SELECTOR);
      existingPriceRangeEls.forEach((existingPriceRangeEl, index) => {
        const newPriceRangeEl = newPriceRangeEls[index];
        if (newPriceRangeEl) {
          existingPriceRangeEl.replaceWith(newPriceRangeEl.cloneNode(true));
        }
      });

      const newFilterEls = newFiltersFormEl.querySelectorAll('[data-facet-filter]');
      const existingFilterEls = filterFormEl.querySelectorAll('[data-facet-filter]');
      existingFilterEls.forEach(existingFilter => {
        const existingFilterId = existingFilter.id;
        const existingFilterInputEl = existingFilter.querySelector('input');
        if (!existingFilterInputEl) return;
        const newFilter = [...newFilterEls].find(newFilter => newFilter.id === existingFilterId);
        if (newFilter) {
          const newFilterInputEl = newFilter.querySelector('input');
          if (!newFilterInputEl) return;
          existingFilterInputEl.disabled = newFilterInputEl.disabled;
          existingFilterInputEl.checked = newFilterInputEl.checked;
          existingFilterInputEl.value = newFilterInputEl.value;
        } else {
          existingFilterInputEl.disabled = true;
        }
      });

      syncFilterAccordionBadges(newFiltersFormEl);
    }
    initPriceRangeSliders();
  };

  const renderPage = async (url) => {
    try {
      const resultsDoc = await fetchResults(getSectionUrl(url));
      renderProductGrid(resultsDoc);
      renderFilters(resultsDoc);
    }
    catch (err) {
      console.error('Error rendering page:', err);
    }
  };

  const renderLoadMorePage = async (url, buttonEl) => {
    try {
      buttonEl.setAttribute(ARIAS.BUSY, 'true');
      buttonEl.setAttribute(ARIAS.DISABLED, 'true');

      const resultsDoc = await fetchResults(getSectionUrl(url));
      const productGridItemsEl = productGridEl.querySelector(PRODUCT_GRID_ITEMS_SELECTOR);
      const newProductGridItemsEl = resultsDoc?.querySelector(PRODUCT_GRID_ITEMS_SELECTOR);
      const newPaginationEl = resultsDoc?.querySelector(PAGINATION_SELECTOR);

      if (!productGridItemsEl || !newProductGridItemsEl || !newPaginationEl) {
        await renderPage(url);
        return;
      }

      [...newProductGridItemsEl.children].forEach(childEl => {
        productGridItemsEl.appendChild(childEl);
      });
      paginationEl.innerHTML = newPaginationEl.innerHTML;
      refreshProductCardEnhancements();
    }
    catch (err) {
      console.error('Error loading more products:', err);
      buttonEl.removeAttribute(ARIAS.BUSY);
      buttonEl.removeAttribute(ARIAS.DISABLED);
    }
  };

  const handleFilterLinkClick = async (e) => {
    e.preventDefault();
    const closestLink = e.target.closest('a');
    if (!closestLink || !closestLink.href) return;
    
    const urlHref = closestLink.href;
    window.history.pushState({}, '', urlHref);
    await renderPage(urlHref);
  };

  const convertFormDataToUrl = () => {
    const formData = new FormData(filterFormEl);
    if (sortByEl) {
      formData.append('sort_by', sortByEl.value);
    }
    const params = new URLSearchParams(formData).toString();
    const url = new URL(window.location.href);
    url.search = params;
    return url;
  };

  const handleFormInput = async () => {
    const url = convertFormDataToUrl();
    if (!url) return;

    window.history.pushState({}, '', url);
    renderPage(url.href);
  };

  const handleDesktopPriceInput = (priceRangeEl) => {
    const minInputEl = priceRangeEl.querySelector('[data-price-range-min-input]');
    const maxInputEl = priceRangeEl.querySelector('[data-price-range-max-input]');
    const url = new URL(window.location.href);

    if (minInputEl?.name) {
      if (minInputEl.value) {
        url.searchParams.set(minInputEl.name, minInputEl.value);
      } else {
        url.searchParams.delete(minInputEl.name);
      }
    }

    if (maxInputEl?.name) {
      if (maxInputEl.value) {
        url.searchParams.set(maxInputEl.name, maxInputEl.value);
      } else {
        url.searchParams.delete(maxInputEl.name);
      }
    }

    window.history.pushState({}, '', url);
    renderPage(url.href);
  };

  const debouncedFormInput = debounce(SETTINGS.FILTER_UPDATE_DELAY, handleFormInput);
  const debouncedDesktopPriceInput = debounce(SETTINGS.FILTER_UPDATE_DELAY, handleDesktopPriceInput);

  const closeFilterDrawer = () => {
    filterDrawer.classList.remove(CLASSES.TRANSLATE_X_0);
    setTimeout(() => {
      filterDrawer.close();
    }, SETTINGS.FILTER_DRAW_ANIMATION_TIME);
  };

  const handleCloseFilterDrawerClick = () => {
    closeFilterDrawer();
  };

  const handleFilterDrawerClick = e => {
    if (!filterDrawer) return;
    const isInDialog = filterDrawer.getBoundingClientRect().right > e.clientX;
    if (!isInDialog) {
      closeFilterDrawer();
    }
  };

  const openFilterDrawer = () => {
    collapseAllAccordions();
    filterDrawer.showModal();
    //uses requestAnimationFrame to ensure the class is added after the modal is displayed (on safari)
    //without this, the transition does not work correctly on Safari
    requestAnimationFrame(() => {
      filterDrawer.classList.add(CLASSES.TRANSLATE_X_0);
    });
    trapFocus(filterDrawer);
    filterDrawer.addEventListener('click', handleFilterDrawerClick);
    filterFormEl.addEventListener('input', debouncedFormInput);
    filterDrawerSubmitBtn?.addEventListener('click', handleCloseFilterDrawerClick);
    filterDrawerClearAllBtn?.addEventListener('click', handleFilterLinkClick);
    closeDrawerBtn?.addEventListener('click', handleCloseFilterDrawerClick);
  };

  const handleAccordionToggle = (accordionToggle) => {
    const accordionPanel = filterFormEl.querySelector(`#${accordionToggle.getAttribute('aria-controls')}`);
    const isExpanded = accordionToggle.getAttribute(ARIAS.EXPANDED) === 'true';
    accordionToggle.setAttribute(ARIAS.EXPANDED, String(!isExpanded));
    if (accordionPanel) {
      accordionPanel.hidden = isExpanded;
    }
  };

  // Map the live handlers to the container so the persistent document-level
  // listener below dispatches to the current init's closure after ACS re-injects
  // the filters container (which orphans node-bound listeners). A WeakMap lets the
  // old container GC cleanly once it's replaced.
  // Each handler closes over this init's filterFormEl, so old/new containers stay
  // scoped to their own filter markup after ACS swaps the DOM.
  filterHandlerMap.set(filtersContainer, { handleFilterLinkClick, handleAccordionToggle });

  if (!isFilterClickDelegationInitialized) {
    document.addEventListener('click', (e) => {
      const container = e.target.closest('[data-filters-container]');
      const handlers = container && filterHandlerMap.get(container);
      if (!handlers) return;

      const filterLink = e.target.closest('[data-filter-option-link]');
      if (filterLink) {
        handlers.handleFilterLinkClick(e);
        return;
      }

      const accordionToggle = e.target.closest('[data-filter-accordion-toggle]');
      if (accordionToggle) {
        handlers.handleAccordionToggle(accordionToggle);
      }
    });
    isFilterClickDelegationInitialized = true;
  }
  
  if (openDrawerBtn) {
    openDrawerBtn.addEventListener('click', openFilterDrawer);
  }

  filtersContainer.addEventListener('input', (e) => {
    const desktopPriceRangeEl = e.target.closest('[data-desktop-price-range]');
    if (!desktopPriceRangeEl) return;

    debouncedDesktopPriceInput(desktopPriceRangeEl);
  });

  paginationEl?.addEventListener('click', (e) => {
    const loadMoreLink = e.target.closest('[data-load-more]');
    if (!loadMoreLink) return;
    if (loadMoreLink.getAttribute(ARIAS.BUSY) === 'true') return;

    e.preventDefault();
    renderLoadMorePage(loadMoreLink.href, loadMoreLink);
  });
  
  sortByEl?.addEventListener('change', handleFormInput);
  initPriceRangeSliders();

  filterDrawer.addEventListener('close', () => {
    filterDrawer.removeEventListener('click', handleFilterDrawerClick);
    filterFormEl.removeEventListener('input', debouncedFormInput);
    filterDrawerSubmitBtn?.removeEventListener('click', handleCloseFilterDrawerClick);
    closeDrawerBtn?.removeEventListener('click', handleCloseFilterDrawerClick);
    filterDrawerClearAllBtn?.removeEventListener('click', handleFilterLinkClick);
    openDrawerBtn?.focus();
  });
};

export default initCollectionSearchFilters;
